import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type FormEvent,
} from "react";
import { LockKeyhole } from "lucide-react";
import { api, ApiError, post } from "./api";
import {
  Button,
  Card,
  ErrorState,
  InlineNotice,
  Modal,
  PageLoading,
} from "./components/ui";
import type { AccountState, AccountProfile } from "./accountTypes";
import { AccountCenter } from "./AccountCenter";
import { useI18n } from "./i18n";

const AccountFeaturesContext = createContext<Record<string, boolean>>({});
export const useAccountFeatures = () => useContext(AccountFeaturesContext);
type Verification = {
  purpose: string;
  resolve: (token: string) => void;
  reject: (error: Error) => void;
};
export function AccountRuntime({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AccountState | null>(null),
    [error, setError] = useState("");
  const latest = useRef(state);
  latest.current = state;
  const lastActivity = useRef(Date.now()),
    lastSent = useRef(0);
  const localLockAt = useRef(0);
  const load = useCallback(async () => {
    try {
      const value = await api<AccountState>("/me/account/state");
      const interaction = new Date(value.interaction_at).getTime();
      if (!value.account.features.lock || interaction > localLockAt.current)
        localLockAt.current = 0;
      if (localLockAt.current) value.locked = true;
      if (Number.isFinite(interaction))
        lastActivity.current = Math.max(lastActivity.current, interaction);
      setState(value);
      setError("");
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 401)) setError(String(e));
    }
  }, []);
  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 15000);
    const channel =
      typeof BroadcastChannel !== "undefined"
        ? new BroadcastChannel("layla-account-activity")
        : null;
    let lastBroadcast = 0;
    const activity = () => {
      const now = Date.now();
      lastActivity.current = now;
      if (channel && now - lastBroadcast > 4000) {
        lastBroadcast = now;
        channel.postMessage({ at: now });
      }
    };
    if (channel)
      channel.onmessage = (e) => {
        if (
          typeof e.data?.at === "number" &&
          Math.abs(Date.now() - e.data.at) < 10000
        )
          lastActivity.current = Math.max(lastActivity.current, e.data.at);
      };
    const controllerActivity = (event: MessageEvent) => {
      if (
        event.origin !== location.origin ||
        event.data?.type !== "LAYLA_ACCOUNT_ACTIVITY"
      )
        return;
      if (
        Array.from(
          document.querySelectorAll(".controller-workspace iframe"),
        ).some(
          (frame) =>
            (frame as HTMLIFrameElement).contentWindow === event.source,
        )
      )
        activity();
    };
    addEventListener("message", controllerActivity);
    const refresh = () => void load();
    addEventListener("layla:account-refresh", refresh);
    addEventListener("pointerdown", activity);
    addEventListener("keydown", activity);
    addEventListener("touchstart", activity, { passive: true });
    const visibility = () => {
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", visibility);
    const heartbeat = setInterval(() => {
      const now = Date.now(),
        s = latest.current;
      if (!s?.account.features.lock || s.locked) return;
      if (
        s.account.lock_minutes > 0 &&
        now - lastActivity.current >= s.account.lock_minutes * 60000
      ) {
        localLockAt.current = now;
        setState((previous) =>
          previous ? { ...previous, locked: true } : previous,
        );
        void post("/me/account/lock", {}).catch(() => undefined);
        return;
      }
      if (
        now - lastActivity.current < 20000 &&
        now - lastSent.current > 14000
      ) {
        lastSent.current = now;
        void post("/me/account/activity", {}).catch(() => undefined);
      }
    }, 5000);
    return () => {
      clearInterval(timer);
      clearInterval(heartbeat);
      channel?.close();
      removeEventListener("message", controllerActivity);
      removeEventListener("layla:account-refresh", refresh);
      removeEventListener("pointerdown", activity);
      removeEventListener("keydown", activity);
      removeEventListener("touchstart", activity);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [load]);
  if (!state)
    return error ? (
      <ErrorState message={error} onRetry={() => void load()} />
    ) : (
      <PageLoading />
    );
  const incomplete =
    state.initial_mode !== "off" &&
    (!state.initial_username_changed || !state.initial_password_changed);
  return (
    <AccountFeaturesContext.Provider value={state.account.features}>
      <VerificationProvider state={state}>
        {state.locked ? (
          <LockedWorkspace
            state={state}
            onUnlocked={async () => {
              localLockAt.current = 0;
              await load();
            }}
          />
        ) : incomplete && state.initial_mode === "require" ? (
          <div className="account-required">
            <InlineNotice tone="warning">
              请先修改初始用户名和密码，完成后即可进入工作区。
            </InlineNotice>
            <AccountCenter restricted />
          </div>
        ) : (
          <>
            {error && (
              <InlineNotice tone="warning">连接暂时异常：{error}</InlineNotice>
            )}
            {incomplete && (
              <InlineNotice tone="warning">
                请通过左下角头像进入个人设置，完成初始用户名和密码修改。
              </InlineNotice>
            )}
            {children}
          </>
        )}
      </VerificationProvider>
    </AccountFeaturesContext.Provider>
  );
}
function LockedWorkspace({
  state,
  onUnlocked,
}: {
  state: AccountState;
  onUnlocked: () => Promise<void>;
}) {
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  async function unlock(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    setBusy(true);
    setError("");
    try {
      await post("/me/account/unlock", {
        password: new FormData(form).get("password"),
      });
      form.reset();
      await onUnlocked();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="account-lock">
      <LockKeyhole size={36} />
      <h1>工作区已锁定</h1>
      <p>当前浏览器及其标签页已锁定，后台任务继续执行。</p>
      <Card>
        <div className="account-form">
          {state.auth_source === "oidc" ? (
            <a
              className="ui-button button-primary"
              href="/api/ui/v1/auth/oidc/start?purpose=unlock"
            >
              通过身份提供方重新验证
            </a>
          ) : (
            <form onSubmit={unlock} className="account-form">
              <label className="field">
                <span>本人密码</span>
                <input
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                />
              </label>
              {error && <InlineNotice tone="error">{error}</InlineNotice>}
              <Button type="submit" variant="primary" disabled={busy}>
                验证并解锁
              </Button>
            </form>
          )}
          <Button
            onClick={() =>
              void post("/logout", {})
                .then(() => location.reload())
                .catch((e) => setError(String(e)))
            }
          >
            退出当前登录
          </Button>
        </div>
      </Card>
    </div>
  );
}
function VerificationProvider({
  children,
  state,
}: {
  children: ReactNode;
  state: AccountState;
}) {
  const { language } = useI18n(),
    en = language === "en";
  const queue = useRef<Verification[]>([]),
    current = useRef<Verification | null>(null);
  const [request, setRequest] = useState<Verification | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const move = () => {
    current.current = queue.current.shift() || null;
    setRequest(current.current);
    setError("");
  };
  useEffect(() => {
    const listener = (event: Event) => {
      queue.current.push((event as CustomEvent<Verification>).detail);
      if (!current.current) move();
    };
    addEventListener("layla:verify-account", listener);
    return () => {
      removeEventListener("layla:verify-account", listener);
      const cancelled = new Error("Verification cancelled");
      current.current?.reject(cancelled);
      queue.current.forEach((item) => item.reject(cancelled));
      queue.current = [];
      current.current = null;
    };
  }, []);
  const close = () => {
    if (busy) return;
    current.current?.reject(
      new Error(en ? "Verification cancelled" : "已取消身份验证"),
    );
    move();
  };
  async function verify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!request) return;
    setBusy(true);
    setError("");
    const form = event.currentTarget,
      data = new FormData(form);
    try {
      const result = await post<{ grant_token: string }>("/me/account/verify", {
        purpose: request.purpose,
        password: data.get("password") || "",
        code: data.get("code") || "",
      });
      form.reset();
      request.resolve(result.grant_token);
      move();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }
  const needCode =
    state.account.totp_enabled ||
    (state.account.totp_bound && Boolean(request?.purpose.startsWith("totp.")));
  return (
    <>
      {children}
      <Modal
        open={Boolean(request)}
        title={en ? "Verify your identity" : "再次验证本人身份"}
        description={
          (
            {
              "totp.bind": "绑定或更换验证器",
              "totp.remove": "解绑验证器",
              "totp.login": "调整登录二次验证",
              "totp.codes": "生成 TOTP 恢复码",
              "password.codes": "生成密码恢复凭据",
              "lock.disable": "关闭账户锁屏",
              "storage.read": "读取存储连接配置",
            } as Record<string, string>
          )[request?.purpose || ""] || "确认本次本人操作"
        }
        onClose={close}
      >
        <form key={request?.purpose} onSubmit={verify} className="account-form">
          {state.auth_source === "oidc" ? (
            <InlineNotice>
              <a href="/api/ui/v1/auth/oidc/start?purpose=verify">
                通过身份提供方重新验证
              </a>
              ，返回后重新发起操作。
            </InlineNotice>
          ) : (
            <label className="field">
              <span>当前密码</span>
              <input
                name="password"
                type="password"
                autoComplete="current-password"
                required
              />
            </label>
          )}
          {needCode && (
            <label className="field">
              <span>新的动态验证码（每次验证码只能成功使用一次）</span>
              <input
                name="code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                required
              />
            </label>
          )}
          {error && <InlineNotice tone="error">{error}</InlineNotice>}
          <div className="form-actions">
            <Button onClick={close} disabled={busy}>
              取消
            </Button>
            <Button variant="primary" type="submit" disabled={busy}>
              验证并继续
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
export function accountChanged() {
  dispatchEvent(new Event("layla:account-refresh"));
  dispatchEvent(new Event("layla:account-updated"));
}
export function avatarURL(profile: AccountProfile) {
  return `/api/ui/v1/me/account/avatar?v=${profile.revision}`;
}
